import { inject, Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { from } from 'rxjs';

import { BaseState } from '../reducers';
import { ShowError, ShowInfo } from '../notification/notification.actions';
import { Go } from '../router/router.actions';
import { errorMessageParser } from '../../gql-client/error-message-parser';
import { MessengerFacade } from '../../../components/legacy/messenger/services/messenger.facade';
import { ConversationSenderTypes } from '../../../components/legacy/messenger/model/enum';
import { messengerNotificationConfig } from '../../../components/legacy/messenger/model/messengerNotificationConfig';
import {
  POLLING_TIME_NEW_MESSAGES_LL,
  POLLING_TIME_NEW_MESSAGES_PS
} from '../../../components/legacy/messenger/model/interface';
import {
  ARCHIVE_CONVERSATION,
  ArchiveConversation,
  ArchiveConversationFail,
  ArchiveConversationSuccess,
  BLOCK_CONVERSATIONS,
  BlockConversation,
  BlockConversationFail,
  BlockConversationSuccess,
  BULK_SEND_MESSAGE,
  BulkSendMessage,
  BulkSendMessageFail,
  BulkSendMessageSuccess,
  CHECK_FOR_CONVERSATION,
  CheckForConversation,
  COUNT_UNREAD,
  CountUnread,
  CountUnreadSuccess,
  CREATE_TEMPLATE,
  CreateConversation,
  CreateTemplate,
  CreateTemplateFail,
  CreateTemplateSuccess,
  DELETE_TEMPLATE,
  DeleteTemplate,
  DeleteTemplateFail,
  DeleteTemplateSuccess,
  DESELECT_CONVERSATION,
  DeselectConversation,
  FIND_UNREAD_MESSAGES,
  FindUnreadMessages,
  FindUnreadMessagesSuccess,
  INCREASE_CHAT_PAGE,
  IncreaseChatPage,
  LOAD_CONVERSATION_MESSAGES,
  LOAD_CONVERSATION_MESSAGES_AND_DETAILS,
  LOAD_CONVERSATION_MESSAGES_AND_DETAILS_SUCCESS,
  LOAD_CONVERSATIONS,
  LOAD_PARSED_TEMPLATES,
  LOAD_PARTICIPATED_AGENTS,
  LOAD_TEMPLATE,
  LoadConversationMessagesAndDetails,
  LoadConversationMessagesAndDetailsFail,
  LoadConversationMessagesAndDetailsSuccess,
  LoadConversations,
  LoadConversationsFail,
  LoadConversationsMessages,
  LoadConversationsMessagesFail,
  LoadConversationsMessagesSuccess,
  LoadConversationsSuccess,
  LoadParsedTemplates,
  LoadParsedTemplatesFail,
  LoadParsedTemplatesSuccess,
  LoadParticipatedAgents,
  LoadParticipatedAgentsFail,
  LoadParticipatedAgentsSuccess,
  LoadTemplate,
  LoadTemplateFail,
  LoadTemplateSuccess,
  MARK_CONVERSATION_AS_UNREAD,
  MarkConversationAsUnreadSuccess,
  REFETCH_COUNT_UNREAD,
  RefetchCountUnread,
  RefetchCountUnreadSuccess,
  SEND_MESSAGE,
  SEND_MESSAGE_IN_NEW_CONVERSATION,
  SendMessage,
  SendMessageFail,
  SendMessageInNewConversation,
  SendMessageInNewConversationFail,
  SendMessageInNewConversationSuccess,
  SendMessageSuccess,
  START_FIND_UNREAD_MESSAGES_POLLING,
  StartFindUnreadMessagesPolling,
  STOP_COUNT_UNREAD_POLLING,
  STOP_FIND_UNREAD_MESSAGES_POLLING,
  StopCountUnreadPolling,
  StopFindUnreadMessagesPolling,
  UN_ARCHIVE_CONVERSATION,
  UnarchiveConversation,
  UnarchiveConversationFail,
  UnarchiveConversationSuccess,
  UPDATE_TEMPLATE,
  UpdateTemplate,
  UpdateTemplateFail,
  UpdateTemplateSuccess
} from './messenger.actions';
import {
  getActiveConversationId,
  getConversationMessagesPage,
  getSender
} from './messenger.selector';

@Injectable()
export class MessengerEffects {
  private actions$ = inject(Actions);
  private messengerRepository = inject(MessengerFacade);
  private store = inject<Store<BaseState>>(Store);

  sendMessageInNewConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SendMessageInNewConversation>(SEND_MESSAGE_IN_NEW_CONVERSATION),
      switchMap(({ data }) =>
        this.messengerRepository.sendMessageInNewConversation(data).pipe(
          map(res => new SendMessageInNewConversationSuccess(res)),
          catchError((error: Error) => {
            return [
              new SendMessageInNewConversationFail(error),
              new ShowError(
                errorMessageParser(
                  error,
                  messengerNotificationConfig.messenger.sendMessage.error
                )
              )
            ];
          })
        )
      )
    )
  );

  sendMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SendMessage>(SEND_MESSAGE),
      switchMap(({ data, unarchive }) =>
        this.messengerRepository.sendMessage(data).pipe(
          mergeMap(res => {
            const actions: Action[] = [new SendMessageSuccess(res)];
            if (unarchive) {
              actions.push(new UnarchiveConversation(data.conversationId));
            }
            return actions;
          }),
          catchError((error: Error) => {
            return [
              new SendMessageFail(error),
              new ShowError(
                errorMessageParser(
                  error,
                  messengerNotificationConfig.messenger.sendMessage.error
                )
              )
            ];
          })
        )
      )
    )
  );

  bulkSendMessage$ = createEffect(() =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.actions$.pipe(
      ofType<BulkSendMessage>(BULK_SEND_MESSAGE),
      switchMap(({ data }) =>
        this.messengerRepository.bulkSendMessage(data).pipe(
          mergeMap(() => [
            new BulkSendMessageSuccess(),
            new ShowInfo(
              messengerNotificationConfig.messenger.bulkSendMessage.success
            )
          ]),
          catchError((error: Error) => [
            new BulkSendMessageFail(error),
            new ShowError(
              messengerNotificationConfig.messenger.bulkSendMessage.error
            )
          ])
        )
      )
    )
  );

  loadConversations$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadConversations>(LOAD_CONVERSATIONS),
      switchMap(action =>
        this.messengerRepository.searchConversation(action.data).pipe(
          map(
            conversations =>
              new LoadConversationsSuccess(
                conversations,
                action.data?.conversationId,
                action.data?.archivedByCustomerOnly
              )
          ),
          catchError(error => [
            new LoadConversationsFail(error),
            new ShowError(
              messengerNotificationConfig.messenger.loadConversations.error
            )
          ])
        )
      )
    )
  );

  blockConversations$ = createEffect(() =>
    this.actions$.pipe(
      ofType<BlockConversation>(BLOCK_CONVERSATIONS),
      switchMap(({ data }) =>
        this.messengerRepository.blockConversation(data).pipe(
          map(() => new BlockConversationSuccess(data.blocked)),
          catchError(error => [
            new BlockConversationFail(error),
            new ShowError(
              messengerNotificationConfig.messenger.loadConversations.error
            )
          ])
        )
      )
    )
  );

  refetchCountOnConversationLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadConversationMessagesAndDetailsSuccess>(
        LOAD_CONVERSATION_MESSAGES_AND_DETAILS_SUCCESS
      ),
      filter(action => action?.refetchCount),
      map(() => new RefetchCountUnread())
    )
  );
  loadConversationsMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadConversationsMessages>(LOAD_CONVERSATION_MESSAGES),
      switchMap(action =>
        this.messengerRepository
          .searchConversationMessagesByConversationId(action.input)
          .pipe(
            withLatestFrom(this.store.select(getSender)),
            mergeMap(([res, sender]) => {
              const POLLING_TIME =
                sender === ConversationSenderTypes.LANDLORD
                  ? POLLING_TIME_NEW_MESSAGES_LL
                  : POLLING_TIME_NEW_MESSAGES_PS;
              return [
                new LoadConversationsMessagesSuccess(res.nodes, res.page),

                new FindUnreadMessages(action.input, POLLING_TIME)
              ];
            }),
            catchError(error => [
              new LoadConversationsMessagesFail(error),
              new ShowError(
                messengerNotificationConfig.messenger.loadConversationMessages
                  .error
              )
            ])
          )
      )
    )
  );

  loadConversationsMessagesAndDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadConversationMessagesAndDetails>(
        LOAD_CONVERSATION_MESSAGES_AND_DETAILS
      ),
      withLatestFrom(this.store.select(getSender)),
      switchMap(([action, sender]) =>
        this.messengerRepository
          .conversationMessagesAndDetails(action.input, sender)
          .pipe(
            mergeMap(res => {
              const POLLING_TIME =
                sender === ConversationSenderTypes.LANDLORD
                  ? POLLING_TIME_NEW_MESSAGES_LL
                  : POLLING_TIME_NEW_MESSAGES_PS;
              return [
                new LoadConversationMessagesAndDetailsSuccess(
                  {
                    nodes: res.searchConversationMessagesByConversationId.nodes,
                    page: res.searchConversationMessagesByConversationId.page,
                    details: res.conversationDetails
                  },
                  action?.refetchCount,
                  action?.input.conversationId
                ),
                new FindUnreadMessages(action.input, POLLING_TIME)
              ];
            }),
            catchError(error => [
              new LoadConversationMessagesAndDetailsFail(error),
              new ShowError(
                messengerNotificationConfig.messenger.loadConversationMessages
                  .error
              )
            ])
          )
      )
    )
  );

  archiveConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ArchiveConversation>(ARCHIVE_CONVERSATION),
      switchMap(({ conversationId }) =>
        this.messengerRepository.archiveConversation(conversationId).pipe(
          mergeMap(() => [
            new ArchiveConversationSuccess(conversationId),
            new ShowInfo(
              messengerNotificationConfig.messenger.archiveConversation.success
            )
          ]),
          catchError((error: Error) => [
            new ArchiveConversationFail(error),
            new ShowError(
              messengerNotificationConfig.messenger.archiveConversation.error
            )
          ])
        )
      )
    )
  );

  unarchiveConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UnarchiveConversation>(UN_ARCHIVE_CONVERSATION),
      switchMap(({ conversationId }) =>
        this.messengerRepository.unarchiveConversation(conversationId).pipe(
          mergeMap(() => [
            new UnarchiveConversationSuccess(conversationId),
            new ShowInfo(
              messengerNotificationConfig.messenger.unarchiveConversation
                .success
            )
          ]),
          catchError((error: Error) => [
            new UnarchiveConversationFail(error),
            new ShowError(
              messengerNotificationConfig.messenger.unarchiveConversation.error
            )
          ])
        )
      )
    )
  );

  loadParticipatedAgents$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadParticipatedAgents>(LOAD_PARTICIPATED_AGENTS),
      switchMap(({ conversationId }) =>
        this.messengerRepository.loadParticipatedAgents(conversationId).pipe(
          map(res => new LoadParticipatedAgentsSuccess(res)),
          catchError(error => [new LoadParticipatedAgentsFail(error)])
        )
      )
    )
  );

  setPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType<IncreaseChatPage>(INCREASE_CHAT_PAGE),
      withLatestFrom(
        this.store.select(getActiveConversationId),
        this.store.select(getConversationMessagesPage)
      ),
      map(
        ([{ customerSettings }, activeConversation, { page }]) =>
          new LoadConversationsMessages({
            conversationId: activeConversation,
            page,
            customerSettings
          })
      )
    )
  );

  findUnreadMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType<FindUnreadMessages>(FIND_UNREAD_MESSAGES),
      switchMap(({ input, pollInterval }) =>
        this.messengerRepository.findUnreadMessages(input, pollInterval).pipe(
          map(({ nodes }) => new FindUnreadMessagesSuccess(nodes)),
          catchError(() => [
            new ShowError(
              messengerNotificationConfig.messenger.findUnreadMessages.error
            )
          ])
        )
      )
    )
  );

  countUnread$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CountUnread>(COUNT_UNREAD),
      switchMap(({ pollInterval }) =>
        this.messengerRepository
          .countUnread(pollInterval)
          .pipe(map(({ messages }) => new CountUnreadSuccess(messages)))
      )
    )
  );

  refetchCountUnread$ = createEffect(() =>
    this.actions$.pipe(
      ofType<RefetchCountUnread>(REFETCH_COUNT_UNREAD),
      switchMap(() =>
        this.messengerRepository
          .refetchCountUnread()
          .pipe(map(({ messages }) => new RefetchCountUnreadSuccess(messages)))
      )
    )
  );

  checkForConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckForConversation>(CHECK_FOR_CONVERSATION),
      switchMap(
        ({
          data: { propertySearcher, property, ticketDetails },
          agentIds,
          selectedAgentId
        }) => {
          if (propertySearcher.conversationId) {
            return this.messengerRepository
              .searchConversation({
                conversationId: propertySearcher?.conversationId,
                agents: agentIds,
                archivedByCustomerOnly: propertySearcher?.archivedByCustomer
              })
              .pipe(
                mergeMap(
                  conversations =>
                    [
                      new LoadConversationsSuccess(
                        conversations,
                        conversations.nodes[0]?.id,
                        propertySearcher?.archivedByCustomer
                      ),
                      new Go({
                        path: ['messenger'],
                        extras: {
                          queryParams: {
                            conversationExists: true,
                            agentId: selectedAgentId
                          }
                        }
                      })
                    ] as Action[]
                )
              );
          } else {
            const actions = [
              new CreateConversation({
                property,
                propertySearcher,
                ticketDetails,
                applicationId: propertySearcher?.id
              }),
              new Go({
                path: ['messenger'],
                extras: {
                  queryParams: {
                    conversationExists: false,
                    applicationId: propertySearcher?.id,
                    ticketId: ticketDetails?.id
                  }
                }
              })
            ] as Action[];

            return from(actions);
          }
        }
      )
    )
  );

  stopCountUnreadPolling = createEffect(
    () =>
      this.actions$.pipe(
        ofType<StopCountUnreadPolling>(STOP_COUNT_UNREAD_POLLING),
        tap(() => this.messengerRepository.stopCountUnreadPolling())
      ),
    { dispatch: false }
  );

  startFindUnreadMessagesPolling = createEffect(
    () =>
      this.actions$.pipe(
        ofType<StartFindUnreadMessagesPolling>(
          START_FIND_UNREAD_MESSAGES_POLLING
        ),
        map(action => action.pollingInterval),
        tap(pollingInterval =>
          this.messengerRepository.startFindUnreadMessagesPolling(
            pollingInterval
          )
        )
      ),
    { dispatch: false }
  );

  stopFindUnreadMessagesPolling = createEffect(
    () =>
      this.actions$.pipe(
        ofType<StopFindUnreadMessagesPolling>(
          STOP_FIND_UNREAD_MESSAGES_POLLING
        ),
        tap(() => this.messengerRepository.stopFindUnreadMessagesPolling())
      ),
    { dispatch: false }
  );

  stopUnreadPollingOnDeselect$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeselectConversation>(DESELECT_CONVERSATION),
      map(() => new StopFindUnreadMessagesPolling())
    )
  );

  loadTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadTemplate>(LOAD_TEMPLATE),
      switchMap(({ page }) =>
        this.messengerRepository.loadTemplate(page).pipe(
          map(templates => new LoadTemplateSuccess(templates)),
          catchError(err => [new LoadTemplateFail(err)])
        )
      )
    )
  );

  loadParsedTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadParsedTemplates>(LOAD_PARSED_TEMPLATES),
      switchMap(({ input }) =>
        this.messengerRepository.loadParsedTemplates(input).pipe(
          map(templates => new LoadParsedTemplatesSuccess(templates)),
          catchError(err => [new LoadParsedTemplatesFail(err)])
        )
      )
    )
  );

  createTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CreateTemplate>(CREATE_TEMPLATE),
      switchMap(({ template }) =>
        this.messengerRepository.createTemplate(template).pipe(
          mergeMap(({ id }) => [
            new CreateTemplateSuccess({ ...template, id }),
            new ShowInfo(messengerNotificationConfig.template.create.success)
          ]),
          catchError(err => [new CreateTemplateFail(err)])
        )
      )
    )
  );

  updateTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateTemplate>(UPDATE_TEMPLATE),
      switchMap(({ template }) =>
        this.messengerRepository.updateTemplate(template).pipe(
          mergeMap(() => [
            new UpdateTemplateSuccess(template),
            new ShowInfo(messengerNotificationConfig.template.update.success)
          ]),
          catchError(err => [new UpdateTemplateFail(err)])
        )
      )
    )
  );

  deleteTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteTemplate>(DELETE_TEMPLATE),
      switchMap(({ templateId }) =>
        this.messengerRepository.deleteTemplate(templateId).pipe(
          mergeMap(() => [
            new DeleteTemplateSuccess(templateId),
            new ShowInfo(messengerNotificationConfig.template.delete.success)
          ]),
          catchError(err => [new DeleteTemplateFail(err)])
        )
      )
    )
  );

  markConversationAsUnread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MARK_CONVERSATION_AS_UNREAD),
      switchMap(({ id }) =>
        this.messengerRepository.markConversationAsUnread(id).pipe(
          map(() => new MarkConversationAsUnreadSuccess(id)),
          catchError(error => [new ShowError(errorMessageParser(error))])
        )
      )
    )
  );
}
